/**
 * The premises-blocked screen — Phase 2.5b, D7.
 *
 * Shown when any API response answers 403 `PREMISES_RESTRICTED`: the user
 * authenticated fine, but the network they are on matches none of their
 * countries' registered premises. The guard bites on use, not on login, so this
 * is the screen that explains the session they can hold but not use.
 *
 * ===========================================================================
 * CALM, AND SILENT ABOUT THE NETWORK — BOTH ARE REQUIREMENTS
 * ===========================================================================
 * · One sentence, and the way forward ("ask your administrator about remote
 *   access"). Being off-premises is an expected state for a field visit or a
 *   home evening, not an incident, so the screen borrows the entry backdrop —
 *   the same designed surface as "you do not have access to this company" —
 *   rather than an error treatment.
 * · NO network details. Not the IP the server judged, not any CIDR, not which
 *   countries have rules. The server's deny body is equally bare (the guard
 *   strips it) — this screen must not undo that by rendering what the client
 *   happens to know. `premises-blocked.test.tsx` asserts the absence.
 * · The screen fires NO requests of its own — it is static JSX, so it cannot
 *   re-trigger the 403 that mounted it. Its one action, sign out, goes through
 *   the session's normal `signOut`, whose server call is best-effort by design
 *   (`lib/api.ts logout`): even when the logout POST is itself refused by the
 *   premises guard, the local session ends and the login screen returns.
 */

import { EntryBackdrop, G1Mark } from '../components/EntryBackdrop';
import { Button } from '../components/ui';

export function PremisesBlockedScreen({
  onSignOut,
}: {
  onSignOut: () => void;
}): React.JSX.Element {
  return (
    <EntryBackdrop>
      <div className="mx-auto flex w-full max-w-[440px] flex-col items-center gap-5 text-center">
        <G1Mark />
        <h1 className="text-h1 text-fg-inverse">This network is outside your workplace</h1>
        <p className="text-body13 text-fg-inverse/70">
          Ask your administrator about remote access if you need to work from here.
        </p>
        <Button variant="ghost-on-dark" onClick={onSignOut}>
          Sign out
        </Button>
      </div>
    </EntryBackdrop>
  );
}
